import { FunctionComponent, useCallback, useEffect, useState } from "react";
import { Alert, Button, Card, Col, Popconfirm, Row, Space, Spin, Statistic, Table, Tag, Tooltip, Typography } from "antd";
import { ColumnsType } from "antd/es/table";
import { CloseCircleOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import useApi from "../../hooks/useApi";
import {
    LIBELLES_MODE_PAIEMENT,
    ModePaiement,
    PaiementBack,
    PaiementFront,
    SituationPaiement,
    SituationPaiementDtoSchema,
    StatutPaiement,
    TypeCiblePaiement,
} from "../../services/paiement";
import {
    PAIEMENTS_ENDPOINT,
    PAIEMENT_ANNULATION_ENDPOINT,
    PAIEMENT_EXISTING_ENDPOINT,
    buildUrlWithParams,
} from "../../services/services";
import { APPLICATION_DATE_FORMAT } from "../../utils/FormUtils";
import { ModalSaisiePaiement } from "./ModalSaisiePaiement";
import { PaiementBadge } from "./PaiementBadge";
import { formatMontant, getLibelleErreurPaiement } from "./paiementUtils";

const { Text } = Typography;

export type PaiementsInscriptionProps = {
    idInscription: number;
    /**
     * Situation déjà connue de l'appelant — le formulaire d'inscription la reçoit embarquée dans
     * l'inscription. Quand elle est fournie, aucun appel de chargement n'est fait.
     */
    situationInitiale?: SituationPaiement;
    onChange?: (situation: SituationPaiement) => void;
};

/**
 * Bloc de gestion des règlements d'une inscription : situation, historique et accès à la saisie.
 *
 * Ne contient volontairement aucun `<Form>` : il est monté à l'intérieur des formulaires
 * d'inscription, eux-mêmes des `<Form>` antd. La saisie vit donc dans une modale, cf.
 * {@link ModalSaisiePaiement}.
 *
 * Chaque mutation renvoyant la situation complète, il n'y a jamais de rechargement après un
 * enregistrement — un seul appel initial, et seulement s'il n'est pas déjà fourni.
 */
export const PaiementsInscription: FunctionComponent<PaiementsInscriptionProps> = ({
    idInscription,
    situationInitiale,
    onChange,
}) => {
    const { execute, isLoading } = useApi();
    const [situation, setSituation] = useState<SituationPaiement | undefined>(situationInitiale);
    const [saisieOuverte, setSaisieOuverte] = useState<boolean>(false);
    const [paiementEnEdition, setPaiementEnEdition] = useState<PaiementBack | undefined>(undefined);
    const [erreur, setErreur] = useState<string | undefined>(undefined);

    const appliquerSituation = useCallback((nouvelle: SituationPaiement) => {
        setSituation(nouvelle);
        onChange?.(nouvelle);
    }, [onChange]);

    const chargerSituation = useCallback(async () => {
        const result = await execute<SituationPaiement>({
            method: "GET",
            url: PAIEMENTS_ENDPOINT,
            params: { typeCible: TypeCiblePaiement.INSCRIPTION, idCible: idInscription },
        }, SituationPaiementDtoSchema);
        if (result.successData) {
            appliquerSituation(result.successData);
        }
    }, [idInscription, appliquerSituation, execute]);

    // Volontairement sur le seul idInscription : ajouter les autres dépendances boucle.
    useEffect(() => {
        if (!situationInitiale) {
            chargerSituation();
        }
    }, [idInscription]);

    useEffect(() => {
        if (situationInitiale) {
            setSituation(situationInitiale);
        }
    }, [situationInitiale]);

    /**
     * Plafond opposable à la saisie. En modification, le paiement édité est déjà compté dans le
     * montant réglé : on le réintègre, sinon toute augmentation serait refusée à tort. C'est la
     * même règle que côté serveur.
     */
    const getMontantMaximum = (): number => {
        const reste = situation?.resteAPayer ?? 0;
        if (paiementEnEdition && paiementEnEdition.statut === StatutPaiement.VALIDE) {
            return reste + paiementEnEdition.montant;
        }
        return reste;
    };

    const ouvrirCreation = () => {
        setErreur(undefined);
        setPaiementEnEdition(undefined);
        setSaisieOuverte(true);
    };

    const ouvrirEdition = (paiement: PaiementBack) => {
        setErreur(undefined);
        setPaiementEnEdition(paiement);
        setSaisieOuverte(true);
    };

    /** @return le message d'erreur à afficher dans la modale, ou undefined en cas de succès. */
    const enregistrer = async (valeurs: PaiementFront): Promise<string | undefined> => {
        const corps = {
            typeCible: TypeCiblePaiement.INSCRIPTION,
            idCible: idInscription,
            montant: valeurs.montant,
            datePaiement: valeurs.datePaiement.format(APPLICATION_DATE_FORMAT),
            mode: valeurs.mode,
            reference: valeurs.reference,
            commentaire: valeurs.commentaire,
        };

        const result = paiementEnEdition
            ? await execute<SituationPaiement>({
                method: "PUT",
                url: buildUrlWithParams(PAIEMENT_EXISTING_ENDPOINT, { id: paiementEnEdition.id }),
                data: corps,
            })
            : await execute<SituationPaiement>({ method: "POST", url: PAIEMENTS_ENDPOINT, data: corps });

        if (result.success && result.successData) {
            appliquerSituation(result.successData);
            return undefined;
        }
        return getLibelleErreurPaiement(result.errorData?.code);
    };

    const annuler = async (paiement: PaiementBack) => {
        setErreur(undefined);
        const result = await execute<SituationPaiement>({
            method: "POST",
            url: buildUrlWithParams(PAIEMENT_ANNULATION_ENDPOINT, { id: paiement.id }),
        });
        if (result.success && result.successData) {
            appliquerSituation(result.successData);
        } else {
            setErreur(getLibelleErreurPaiement(result.errorData?.code));
        }
    };

    const colonnes: ColumnsType<PaiementBack> = [
        {
            title: "Date",
            dataIndex: "datePaiement",
            key: "datePaiement",
            width: 110,
            render: (value: string) => <Text style={{ fontVariantNumeric: "tabular-nums" }}>{value}</Text>,
        },
        {
            title: "Mode",
            dataIndex: "mode",
            key: "mode",
            width: 110,
            render: (value: ModePaiement) => <Tag style={{ borderRadius: 4 }}>{LIBELLES_MODE_PAIEMENT[value]}</Tag>,
        },
        {
            title: "Référence",
            dataIndex: "reference",
            key: "reference",
            width: 140,
            render: (value?: string) => value ? <Text code>{value}</Text> : <Text type="secondary">—</Text>,
        },
        {
            title: "Montant",
            dataIndex: "montant",
            key: "montant",
            width: 110,
            align: "right",
            render: (value: number) => <Text strong style={{ fontVariantNumeric: "tabular-nums" }}>{formatMontant(value)}</Text>,
        },
        {
            title: "Statut",
            dataIndex: "statut",
            key: "statut",
            width: 100,
            render: (value: StatutPaiement) => value === StatutPaiement.ANNULE
                ? <Tag color="default" style={{ borderRadius: 4 }}>Annulé</Tag>
                : <Tag color="success" style={{ borderRadius: 4 }}>Valide</Tag>,
        },
        {
            title: "Actions",
            key: "actions",
            width: 90,
            render: (_, paiement) => {
                const fige = paiement.statut === StatutPaiement.ANNULE;
                return (
                    <Space size={4} wrap={false}>
                        <Tooltip title={fige ? "Un paiement annulé n'est plus modifiable" : "Modifier"}>
                            <Button
                                size="small"
                                icon={<EditOutlined />}
                                disabled={fige}
                                onClick={() => ouvrirEdition(paiement)}
                                {...({ testid: `modifier-paiement-${paiement.id}` } as any)}
                            />
                        </Tooltip>
                        <Popconfirm
                            title="Annuler ce paiement ?"
                            description="Il restera visible dans l'historique mais ne sera plus compté."
                            okText="Annuler le paiement"
                            cancelText="Retour"
                            disabled={fige}
                            onConfirm={() => annuler(paiement)}
                        >
                            <Tooltip title={fige ? "Déjà annulé" : "Annuler"}>
                                <Button
                                    size="small"
                                    danger
                                    icon={<CloseCircleOutlined />}
                                    disabled={fige}
                                    {...({ testid: `annuler-paiement-${paiement.id}` } as any)}
                                />
                            </Tooltip>
                        </Popconfirm>
                    </Space>
                );
            },
        },
    ];

    const resteAPayer = situation?.resteAPayer ?? 0;

    return (
        <Spin spinning={isLoading}>
            <Card size="small" style={{ marginBottom: 16 }}>
                <Row gutter={[24, 16]}>
                    <Col xs={24} sm={8}>
                        <Statistic
                            title={<span style={{ fontSize: 12, color: "rgba(0, 0, 0, 0.45)" }}>Montant dû</span>}
                            value={situation?.montantTotal ?? 0}
                            precision={2}
                            suffix="€"
                            valueStyle={{ fontSize: 20, fontWeight: 700 }}
                        />
                    </Col>
                    <Col xs={24} sm={8}>
                        <Statistic
                            title={<span style={{ fontSize: 12, color: "rgba(0, 0, 0, 0.45)" }}>Déjà réglé</span>}
                            value={situation?.montantRegle ?? 0}
                            precision={2}
                            suffix="€"
                            valueStyle={{ fontSize: 20, fontWeight: 700, color: "#52c41a" }}
                        />
                    </Col>
                    <Col xs={24} sm={8}>
                        <Statistic
                            title={<span style={{ fontSize: 12, color: "rgba(0, 0, 0, 0.45)" }}>Reste à payer</span>}
                            value={resteAPayer}
                            precision={2}
                            suffix="€"
                            valueStyle={{ fontSize: 20, fontWeight: 700, color: resteAPayer > 0 ? "#fa8c16" : "#52c41a" }}
                        />
                    </Col>
                </Row>
                <div style={{ marginTop: 12 }}>
                    <PaiementBadge
                        montantTotal={situation?.montantTotal}
                        montantRegle={situation?.montantRegle}
                        detaille={false}
                    />
                </div>
            </Card>

            <Table<PaiementBack>
                columns={colonnes}
                dataSource={situation?.paiements ?? []}
                rowKey={(paiement) => paiement.id}
                size="small"
                pagination={false}
                scroll={{ x: "max-content" }}
                locale={{ emptyText: "Aucun paiement enregistré" }}
                rowClassName={(paiement) => paiement.statut === StatutPaiement.ANNULE ? "paiement-annule" : ""}
            />

            {erreur && <Alert type="error" showIcon message={erreur} style={{ marginTop: 12 }} />}

            <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={ouvrirCreation}
                disabled={resteAPayer <= 0}
                style={{ marginTop: 16 }}
                {...({ testid: "ajouter-paiement" } as any)}
            >
                {resteAPayer <= 0 ? "Inscription soldée" : "Saisir un paiement"}
            </Button>

            <ModalSaisiePaiement
                open={saisieOuverte}
                setOpen={setSaisieOuverte}
                paiement={paiementEnEdition}
                montantMaximum={getMontantMaximum()}
                onEnregistrer={enregistrer}
            />
        </Spin>
    );
};
